(ns app.front.view.periodic-inspection
  (:require [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.form.add :as add-form]
            [app.front.form.subs :as form-subs]
            [app.front.process :as process]
            [app.front.requirement.subs :as requirement-subs]
            [app.front.ui :as ui]
            [app.front.ui.actions :as ui-actions]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.separator :as ui-sep]
            [app.front.ui.table :as ui-table]
            [app.front.ui.termination-info :as termination-info]
            [app.front.util :as futil]
            [app.front.view.application.page-content :as app-page]
            [app.front.view.approval.core :as approval]
            [app.front.view.attachment.list :as attachment-list]
            [app.front.view.attachment.subs :as attachment-subs]
            [app.front.view.fee.list :as fee-list]
            [app.front.view.form.list :as form-list]
            [app.front.view.letter.section :as letter-section]
            [app.front.view.package.package-section :as package-section]
            [app.front.view.party.invite-v2 :as invite]
            [app.front.view.party.party-section-v2 :as party-section-v2]
            [app.front.view.party.subs :as party-subs]
            [app.front.view.phase-event.section :as phase-event]
            [app.front.view.phase-event.subs :as phase-event-subs]
            [app.front.view.phase-result :as phase-result]
            [app.front.view.workspace.phase-errors :as phase-errors]
            [app.front.view.workspace.section :as ws-section]
            [app.front.view.workspace.subs :as ws-subs]
            [app.front.view.workspace.util :as ws-util]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-fx ::update-target-month
  (fn [_ [_ fsm-target month]]
    {:dispatch [::process/signal
                fsm-target
                [:process.signal/update-inspection-target-month {:target-month month}]
                {:notify {:success :periodic-parent/target-month-updated}}]}))

(def ^:private open-attachments-section-fx
  [[:dispatch [::section/toggle-collapsed (section/get-section-id :periodic-parent-attachments) false]]
   [::futil/scroll-to-section :periodic-parent-attachments]])

(rf/reg-event-fx ::scroll-to-attachments
  (fn [{:keys [db]} _]
    {:db (assoc db ::attachments-section-open? true)
     :fx open-attachments-section-fx}))



(rf/reg-event-db ::reset-ui-state
  (fn [db _]
    (dissoc db ::attachments-section-open?)))

(rf/reg-sub ::attachments-section-open?
  (fn [db _]
    (get db ::attachments-section-open? false)))

(rf/reg-event-fx ::trigger-add-attachment-requirement
  (fn [{:keys [db]} _]
    {:db (assoc db ::attachments-section-open? true)
     :fx (conj open-attachments-section-fx
               [:dispatch [:app.front.requirement.components/open-add-requirement-popup]])}))

;; Workspace children or siblings. Filter out siblings created after currently viewed workspace.
(rf/reg-sub ::workspace-family
  :<- [::ws-subs/workspace]
  (fn [workspace]
    (let [parent-ws  (:workspace/parent-workspace workspace)
          workspaces (if parent-ws
                       (workspace-model/sibling-workspaces workspace)
                       (:workspace/_parent-workspace workspace))]
      (->> workspaces
           seq
           (sort-by :entity/created >)))))

(defn inspection-workspace-state [ws]
  (-> ws
      workspace-model/workspace-execution
      :domain/state))

(defn closing-info [{:keys [fsm-target] :as props}]
  [termination-info/termination-info fsm-target (assoc props
                                                       :reason-attr :execution/closing-reason
                                                       :by-attr :execution/closed-by
                                                       :ts-attr :execution/closed-ts
                                                       :descr-tr-key :execution/closed-by)])

(defn status-ts+by [execution]
  (let [keys (cond
               (du/in-state? :execution.state/complete execution)
               [:execution/completed-ts :execution/completed-by]
               (du/in-state? :execution.state/closed execution)
               [:execution/closed-ts :execution/closed-by])]
    (when keys
      (-> execution (select-keys keys) vals))))

(defn inspection-month+year [tr {:execution/keys [inspection-due] :as execution}]
  (let [tz                   (-> execution group-model/get-authority-group :group/tz)
        {:keys [month year]} (-> inspection-due (date-util/format-to-parts tz))
        month-str            (->> month
                                  dec
                                  str
                                  (keyword "date.month")
                                  vector
                                  tr)]
    [month-str year]))

(defn inspection-month-field [tr execution]
  (let [[month year] (inspection-month+year tr execution)]
    [:div
     [:label.bx--label (tr [:periodic-parent/target-month])]
     [:div (str month " " year)]]))

(defn inspection-workspace-date [tr ws]
  (let [execution    (workspace-model/workspace-execution ws)
        time-fn      (<sub [::date-util/date-time-fn])
        [month year] (inspection-month+year tr execution)]
    (if-let [[ts by] (status-ts+by execution)]
      [:div (time-fn ts) " " (user-model/full-name by)]
      [:div month " " year])))

(defn to-parent-button
  "Render button if the user has permission to view the parent."
  [tr execution]
  (when-let [href (some-> execution
                          :entity/parent
                          :workspace/parent-workspace
                          ws-util/workspace-href)]
    [ui/button
     {:id   "back-to-parent"
      :kind :secondary
      :href href}
     (tr [:periodic-inspection/back-to-parent])]))

(defn workspace-table
  "In periodic parent ws this table lists all child workspaces. In inspection ws list siblings, omit
  viewed ws and all siblings created later than viewed ws."
  [{:keys [tr execution]}]
  (when-let [workspaces (<sub [::workspace-family])]
    [:div
     [ui-sep/horizontal-line]
     (when (and execution (seq workspaces))
       [:div.mb-3>strong (tr [:periodic-inspection/past-results])])
     [ui-table/modern-responsive-table
      (util/mmap {:items      workspaces
                  :get-status #(let [state (inspection-workspace-state %)]
                                 (case state
                                   :execution.state/to-be-scheduled :warning
                                   :execution.state/past-due :critical
                                   :execution.state/complete :ok
                                   nil))
                  :cols       [[:periodic-parent/status
                                #(-> % inspection-workspace-state vector tr)]
                               [:periodic-parent/date
                                (partial inspection-workspace-date tr)]]
                  :link       {:make-href   ws-util/workspace-or-resume-application-href
                               :description ws-util/workspace-link-description}}
                 tr)]]))

(defn periodic-inspection-section-in-child [{:keys [tr execution] :as state}]
  [ws-section/section
   execution
   :periodic-parent-workspaces
   (util/mmap tr)
   :periodic-parent/inspections
   false
   [:div.flex.flex-col.gap-y-4
    [closing-info state]
    [:div.flex.flex-row.gap-x-8
     [inspection-month-field tr execution]
     [to-parent-button tr execution]]
    [workspace-table state]]])

(defn inspection-month-select [{:keys [tr periodic-parent]}]
  [ui-select/select {:id           "inspection-month-select"
                     :label-text   (tr [:periodic-parent/target-month])
                     :values       (range 1 13)
                     :val->str     #(tr [(keyword "date.month" (str (dec %)))])
                     :allow-empty? false
                     :disabled     (not (<sub [::process/periodic-parent-signal-permitted? [:process.signal/update-inspection-target-month]]))
                     :value        (:periodic-parent/inspection-target-month periodic-parent)
                     :on-change    #(>evt [::update-target-month periodic-parent %])}])

(defn inspection-section-in-parent [{:keys [loading? workspace tr periodic-parent] :as state}]
  (let [ui-improvements? (<sub [::ff/enabled? :feature-flag.id/inspection-ui-improvements])
        workspaces       (<sub [::workspace-family])
        has-workspaces?  (seq workspaces)
        show-section?    (if ui-improvements?
                           has-workspaces?
                           true)]
    (when show-section?
      [ws-section/section
       periodic-parent
       :periodic-parent-workspaces
       (util/mmap {:heading-button [ui-actions/signal-button
                                    (util/mmap
                                      {:signal        [:process.signal/create-inspection-workspace {}]
                                       :button-opts   {:kind :ghost
                                                       :icon i/plus}
                                       :target-entity periodic-parent}
                                      tr)]
                   :heading-tag    (-> workspace :workspace/_parent-workspace count)}
                  tr loading?)
       :periodic-parent/inspections
       false
       [:div.flex.flex-col.gap-y-4
        [inspection-month-select state]
        [workspace-table state]]])))

(defn forms-section [{:keys [loading? workspace tr periodic-parent]}]
  (let [auth-group-id     (-> periodic-parent
                              group-model/get-authority-group
                              :db/id)
        process-target-id (:db/id periodic-parent)
        has-forms?        (<sub [::form-subs/has-forms? periodic-parent])
        can-add?          (<sub [::process/signal-permitted? process-target-id [:process.signal/add-form]])
        ui-improvements?  (<sub [::ff/enabled? :feature-flag.id/inspection-ui-improvements])
        show-section?     (if ui-improvements?
                            has-forms?
                            (or has-forms? can-add?))]
    (when show-section?
      (let [heading-button [add-form/add-button {:process-target-id  process-target-id
                                                 :authority-group-id auth-group-id
                                                 :opts               {:kind :ghost
                                                                      :icon i/plus}
                                                 :button-text        (tr [:process.signal/add-form])
                                                 :add-text           (tr [:add])
                                                 :cancel-text        (tr [:cancel])}]]
        [ws-section/section
         periodic-parent
         :application-data
         (util/mmap {:heading-button    heading-button
                     :heading-tag       (<sub [::form-subs/phase-forms-count periodic-parent])
                     :heading-tag-color (if (<sub [::form-subs/missing-any-data? periodic-parent]) :yellow :green)}
                    tr loading?)
         :periodic-parent/forms
         false
         [form-list/forms
          (util/mmap {:parent periodic-parent
                      :lang   (<sub [:language])}
                     tr
                     workspace
                     loading?)]]))))


(defn phase-periodic-parent [{:keys [tr periodic-parent workspace pre-selected-entity-id loading?] :as state}]
  (let [attachments?     (<sub [::attachment-subs/has-attachments? (:db/id periodic-parent)])
        ui-improvements? (<sub [::ff/enabled? :feature-flag.id/inspection-ui-improvements])
        parties          (<sub [::party-subs/parties (:db/id periodic-parent)])]
    [:<>
     [add-form/add-form-modal {:tr tr :process-target periodic-parent}]
     (when (and ui-improvements? (empty? parties))
       [invite/invite-modal tr periodic-parent parties])
     [app-page/page-content
      (util/mmap {:process-target periodic-parent
                  :process        (<sub [::process/periodic-parent-process])}
                 workspace
                 tr
                 loading?)
      [phase-errors/errors tr [::process/periodic-parent-process]]
      (when (<sub [::phase-event-subs/show-phase-event-section? (:db/id periodic-parent)])
        [phase-event/section (util/mmap {:phase periodic-parent} state)])
      [inspection-section-in-parent state]
      [party-section-v2/parties (util/mmap {:parent periodic-parent} tr)]
      [forms-section state]
      (let [attachments-section-open? (<sub [::attachments-section-open?])
            requirements?             (seq (<sub [::requirement-subs/open-attachment-requirements (:db/id periodic-parent)]))
            show-attachments?         (if ui-improvements?
                                        (or attachments? attachments-section-open? requirements?)
                                        (or attachments?
                                            requirements?
                                            (<sub [::process/signal-permitted? periodic-parent [:process.signal/attachment-create-with-version]])))
            initially-collapsed?      (not (or attachments-section-open? pre-selected-entity-id))]
        (when show-attachments?
          [ws-section/section
           periodic-parent
           :periodic-parent-attachments
           (util/mmap tr loading?)
           :view/application-attachments
           initially-collapsed?
           ^{:key "periodic-parent-attachments"}
           [attachment-list/list-view
            {:target          periodic-parent
             :pre-selected-id pre-selected-entity-id}]]))
      [letter-section/letters-section (util/mmap {:target-id (:db/id periodic-parent)} tr)]
      (when (<sub [::ws-subs/has-feature? :fees-and-payments?])
        [fee-list/fees-and-payments-section (util/mmap tr loading?) periodic-parent])
      [approval/approvals state]
      (when (<sub [::process/periodic-parent-signal-permitted? [:process.signal/create-admin-package]])
        [package-section/packages (assoc state :target-entity periodic-parent)])
      [phase-result/result-section state]]]))
